import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Database, Download, RotateCcw, Plus, HardDrive, Table2, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBackupsPage = () => {
  const { user } = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchBackups();
  }, []);

  const fetchBackups = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/backups`, { withCredentials: true });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching backups:', error);
      toast.error('Erro ao carregar informação de backups');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    setCreating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/backups/create`, { backup_type: 'manual' }, { withCredentials: true });
      toast.success('Backup criado com sucesso');
      fetchBackups();
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error('Erro ao criar backup');
    } finally {
      setCreating(false);
    }
  };

  const getTotalDocuments = () => {
    if (!data?.database_stats) return 0;
    return Object.values(data.database_stats).reduce((sum, count) => sum + count, 0);
  };

  const getCollectionIcon = (name) => {
    const icons = {
      users: '👥',
      consents: '📄',
      audit_logs: '📋',
      notifications: '🔔',
      tenants: '🏢',
      custom_roles: '🔐',
      user_sessions: '🔑',
      consent_history: '📜',
      qr_tokens: '📱',
      otp_codes: '🔢'
    };
    return icons[name] || '📁';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#10B981]" />
        <span className="ml-2">A carregar...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-backups-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Gestão de Backups
          </h1>
          <p className="text-slate-600">
            Estatísticas da base de dados e gestão de backups
          </p>
        </div>
        
        <Button
          data-testid="create-backup-button"
          onClick={handleCreateBackup}
          disabled={creating}
          className="bg-[#10B981] hover:bg-[#10B981]/90"
        >
          {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
          {creating ? 'A criar...' : 'Criar Backup Manual'}
        </Button>
      </div>

      {/* Status Card */}
      <Card className={`border-l-4 ${data?.backup_enabled ? 'border-l-green-500' : 'border-l-amber-500'}`}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            {data?.backup_enabled ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <AlertCircle className="w-5 h-5 text-amber-500" />
            )}
            Estado do Sistema de Backups
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-600">
                {data?.backup_enabled 
                  ? 'Backups automáticos estão ativos' 
                  : 'Backups automáticos não configurados'}
              </p>
              {data?.last_backup && (
                <p className="text-sm text-slate-500 mt-1">
                  Último backup: {new Date(data.last_backup).toLocaleString('pt-PT')}
                </p>
              )}
            </div>
            <Badge variant={data?.backup_enabled ? 'default' : 'secondary'}>
              {data?.backup_enabled ? 'Ativo' : 'Inativo'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Database Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Documentos</p>
                <p className="text-3xl font-bold text-[#0F172A]">{getTotalDocuments().toLocaleString()}</p>
              </div>
              <HardDrive className="w-10 h-10 text-[#10B981]" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Coleções</p>
                <p className="text-3xl font-bold text-blue-600">
                  {data?.database_stats ? Object.keys(data.database_stats).length : 0}
                </p>
              </div>
              <Table2 className="w-10 h-10 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Base de Dados</p>
                <p className="text-xl font-bold text-purple-600">MongoDB</p>
              </div>
              <Database className="w-10 h-10 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Collections Detail */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            Coleções da Base de Dados
          </CardTitle>
          <CardDescription>
            Estatísticas detalhadas de cada coleção
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data?.database_stats && Object.entries(data.database_stats)
              .sort((a, b) => b[1] - a[1])
              .map(([collection, count]) => {
                const percentage = (count / getTotalDocuments()) * 100 || 0;
                return (
                  <div key={collection} className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{getCollectionIcon(collection)}</span>
                        <span className="font-medium text-sm">{collection}</span>
                      </div>
                      <Badge variant="outline">{count}</Badge>
                    </div>
                    <Progress value={percentage} className="h-2" />
                    <p className="text-xs text-slate-500 mt-1">{percentage.toFixed(1)}% do total</p>
                  </div>
                );
              })}
          </div>
        </CardContent>
      </Card>

      {/* Retention Policy */}
      <Card>
        <CardHeader>
          <CardTitle>Política de Retenção</CardTitle>
          <CardDescription>Configuração recomendada para backups automáticos</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
              <Database className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-[#0F172A]">10</div>
              <p className="text-sm text-slate-600">Backups Diários</p>
              <p className="text-xs text-slate-500 mt-1">Últimos 10 dias</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
              <Database className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-[#0F172A]">4</div>
              <p className="text-sm text-slate-600">Backups Semanais</p>
              <p className="text-xs text-slate-500 mt-1">Últimas 4 semanas</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
              <Database className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-[#0F172A]">3</div>
              <p className="text-sm text-slate-600">Backups Mensais</p>
              <p className="text-xs text-slate-500 mt-1">Últimos 3 meses</p>
            </div>
          </div>
          <div className="mt-6 p-4 bg-slate-50 rounded-lg text-center">
            <p className="text-sm text-slate-600">
              💡 <strong>Recomendação:</strong> Configure backups automáticos para execução diária às 02:00 UTC
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminBackupsPage;
